/**
 * TIMESHEET EXPORTS + THE PAYROLL RUN — the FILE shapes (owner 2026-10-28,
 * `_wip/1028/mock-ts-export.html`). Pure: the export document comes from
 * `GET /api/timesheets/export` (`server/helpers/timesheetExport.js`) and this
 * module only LAYS IT OUT. 💷 It never computes money — every figure, subtotal
 * and total is the server's, printed as sent (`roundMoney` only formats).
 *
 * 📄 CSV (owner ruling A, 2026-10-28; was labelled "Excel" and saved `.xls` until the same-day CSV rename): ONE `.csv` in the house shape — a BOM'd
 * CSV through `rowsToCsv` / `csvEscape` / `downloadTextFile`, the orders/hires
 * exports' exact file (`utils/hireListExport.js`). A CSV has one sheet, so each
 * section is a TITLED BLOCK (title row, header row, rows, blank row) in the ruled
 * order: Payroll overview · Pay summary · Detail · then Outstanding and the
 * footnote. Money is 2dp TEXT so a column still sums in Excel.
 *
 * 🖨️ PDF (ruling f): `utils/listPdf.js#buildListDoc` (A4 landscape, the house
 * list) for the page and the FIRST table; every further section is a heading
 * plus `buildListContent`'s TABLE alone. One row per record everywhere — the
 * pay summary carries a Sub-department column (sorted by it), the overview is
 * one row per sub-department with the department total as a bold line under it.
 * 🔀 R2 F6 (2026-10-28): passes `showScopeLine: false` — listPdf's "All N … matching
 * the current filters" sentence is false of an export (its own scope line says what it is).
 * 🔀 R2 F3: a submitted week whose snapshot the server could not read
 * (`snapshot_unreadable`) is its OWN list — "reopen and resubmit" — never "Needs a rate".
 *
 * 📂 Grouped by the person's HOME sub-department — said on every output.
 * 🧾 Every output carries the "not calculated" footnote.
 */
import { rowsToCsv, downloadTextFile, CSV_EXT, CSV_MIME } from './gagCsvExport';
import { buildListDoc, buildListContent, td, text } from './listPdf';
import { sumHours } from './timesheetHoursSum';
import { roundMoney } from './money';
import { moneyOrAbsent, EM_DASH } from './moneyOrAbsent';
import { formatDate, formatDateTime, formatDateWeekdayDayMonth, toLocalISO } from './dateFormat';
import {
  CREW_STATE_LABELS, TIMESHEET_AGREEMENT_OPTIONS, agreementFamilyValue, awayReasonLabel, fieldLabel, formatFieldValue, weekStatusLabel,
} from './timesheetDisplay';

/** The three sections a manager can tick, in the ruled output order. */
export const EXPORT_SECTIONS = Object.freeze([
  { id: 'overview', label: 'Payroll run overview', short: 'sub-dept totals', hint: 'totals per sub-department and for the department' },
  { id: 'summary', label: 'Pay summary', short: '£ per person-week', hint: 'one row per person per week — each pay line in £' },
  { id: 'detail', label: 'Timesheet detail', short: 'per day', hint: 'one row per person per day — times, O/T hours, penalties, notes' },
]);

/** The server's `pay_columns`, in words (house vocabulary — `utils/timesheetDisplay.js`). */
export const PAY_COLUMN_LABELS = Object.freeze({
  base: 'Day rate pay',
  camera_ot: 'Camera O/T',
  non_camera_ot: 'Non-camera O/T',
  travel: 'Travel',
  meal_penalty: 'Meal pen.',
  night_penalty: 'Night',
  broken_turnaround: 'Broken t/a',
});

export const NOT_CALCULATED_TEXT =
  'Calculated by Crew Forge from the timesheets and this production’s agreement. '
  + 'Not included: holiday pay, allowances, kit/box rental, and PAYE / limited-company status.';

export const GROUPED_BY_TEXT = 'Grouped by home sub-department';

const payColumns = (doc) => (Array.isArray(doc.pay_columns) ? doc.pay_columns : Object.keys(PAY_COLUMN_LABELS));

/** 2dp text for a sheet cell; absent → an empty field (the house CSV marker). */
function csvMoney(v) {
  if (v === null || v === undefined || v === '') return null;
  const r = roundMoney(v);
  return r === null ? String(v) : r.toFixed(2);
}

const weekLabel = (ymd) => `w/c ${formatDate(ymd)}`;

/** "w/c 2 Nov 2026" or "w/c 2 Nov 2026 – w/c 9 Nov 2026 (2 weeks)". */
export function rangeLabel(doc) {
  const weeks = doc.weeks || [];
  if (weeks.length <= 1) return weekLabel(doc.from);
  return `${weekLabel(doc.from)} – ${weekLabel(doc.to)} (${weeks.length} weeks)`;
}

export function agreementLabel(family) {
  if (!family) return null;
  const o = TIMESHEET_AGREEMENT_OPTIONS.find((x) => x.value === agreementFamilyValue(family));
  return o ? o.label : null;
}

export function basisText(doc) {
  return doc.include_projected
    ? 'Includes weeks not yet submitted, marked Projected'
    : 'Submitted weeks only';
}

function peopleText(doc) {
  const f = doc.filters || {};
  if (Array.isArray(f.sub_departments) && f.sub_departments.length) return `Sub-departments: ${f.sub_departments.join(', ')}`;
  if (Array.isArray(f.lineage_ids) && f.lineage_ids.length) return `Chosen people (${f.lineage_ids.length})`;
  return 'Everyone';
}

/** The one-line scope a reader needs to trust the sheet: weeks · people · basis · grouping. */
export function exportScopeText(doc) {
  return [rangeLabel(doc), peopleText(doc), basisText(doc), GROUPED_BY_TEXT].join(' · ');
}

function exportedText(doc) {
  const when = doc.exported_at ? formatDateTime(doc.exported_at) : null;
  return `Exported ${when || ''}${doc.exported_by ? ` by ${doc.exported_by}` : ''}`.trim();
}

const departmentLine = (doc) => [doc.department_name, doc.production_name].filter(Boolean).join(' · ');
const basisLabel = (r) => (r.basis === 'projected' ? 'Projected' : 'Submitted');
// The server's two reasons a person-week has no total (R2 F3): a rate is missing, or the
// submitted week's stored snapshot cannot be read — different fixes, never one list.
export const SNAPSHOT_UNREADABLE_TEXT = 'Snapshot unreadable — reopen and resubmit';
const isUnreadable = (r) => r.snapshot_unreadable === true;
const needsRate = (r) => r.total === null && !isUnreadable(r);
const noTotalText = (r) => (isUnreadable(r) ? 'Snapshot unreadable' : 'Rate missing');
const multiWeek = (doc) => (doc.weeks || []).length > 1;

function unitNameFor(doc) {
  const byId = new Map((doc.units || []).map((u) => [Number(u.id), u.name]));
  return (id) => (id == null ? null : byId.get(Number(id)) || null);
}

const timeRange = (a, b) => (a || b ? `${a || '?'}–${b || '?'}` : null);
const tick = (v) => (v === true || v === '1' || v === 1 ? 'Yes' : null);

/** The detail's rows: every WORKED or AWAY day of every exported person-week. */
export function detailRows(doc) {
  const unitName = unitNameFor(doc);
  const out = [];
  for (const r of doc.person_weeks || []) {
    for (const d of r.days || []) {
      if (!d.worked && !d.away_reason) continue;
      const v = d.values || {};
      out.push({ person: r, day: d, values: v, unit: unitName(d.unit_id) || r.sub_department });
    }
  }
  return out;
}

function dayTypeText(x) {
  if (x.day.away_reason) return `Away · ${awayReasonLabel(x.day.away_reason)}`;
  return formatFieldValue('day_type', x.values.day_type) || null;
}

const jobText = (d) => d.gag || d.job_text || null;

// ── CSV ───────────────────────────────────────────────────────────────────

function overviewBlock(doc) {
  const cols = payColumns(doc);
  const head = ['Sub-department', 'People', 'Days', '6th/7th', ...cols.map((c) => PAY_COLUMN_LABELS[c] || c), 'Total', 'Rate missing'];
  const line = (name, g) => [name, g.people, g.days_worked, g.premium_days, ...cols.map((c) => csvMoney(g.pay[c])), csvMoney(g.total), g.rate_missing || null];
  const groups = (doc.overview && doc.overview.groups) || [];
  return [
    ['Payroll run overview'],
    head,
    ...groups.map((g) => line(g.sub_department, g)),
    line(`${doc.department_name || 'Department'} total`, doc.overview.total),
  ];
}

function summaryBlock(doc) {
  const cols = payColumns(doc);
  return [
    ['Pay summary'],
    ['Week commencing', 'Name', 'Grade', 'Sub-department', 'Days', '6th/7th', ...cols.map((c) => PAY_COLUMN_LABELS[c] || c), 'Total', 'Rate missing', 'Basis', 'Status'],
    ...(doc.person_weeks || []).map((r) => [
      r.week_start, r.name, r.grade, r.sub_department, r.days_worked, r.premium_days,
      ...cols.map((c) => csvMoney(r.pay[c])), csvMoney(r.total), needsRate(r) ? 'Yes' : (isUnreadable(r) ? SNAPSHOT_UNREADABLE_TEXT : null),
      basisLabel(r), weekStatusLabel(r.status),
    ]),
  ];
}

function detailBlock(doc) {
  return [
    ['Timesheet detail'],
    ['Date', 'Day', 'Name', 'Sub-department', 'Day type', 'Working day', 'Unit call', 'Meal start', 'Meal end', 'Wrap', 'Pre-call', 'Final out',
      'Camera O/T (h)', 'Non-camera O/T (h)', 'Travel to (h)', 'Travel from (h)', 'Meal penalty', 'Night penalty', 'Job', 'Notes', 'Basis'],
    ...detailRows(doc).map((x) => {
      const v = x.values;
      return [
        x.day.date, formatDateWeekdayDayMonth(x.day.date).split(' ')[0], x.person.name, x.unit, dayTypeText(x),
        formatFieldValue('working_day', v.working_day), v.unit_call, v.meal_start, v.meal_end, v.unit_wrap, v.in_time, v.out_time,
        v.camera_ot_hours, v.non_camera_ot_hours, v.travel_to_hours, v.travel_from_hours, tick(v.meal_penalty), tick(v.night_penalty),
        jobText(x.day), x.day.notes, basisLabel(x.person),
      ];
    }),
  ];
}

function outstandingBlock(doc) {
  const list = doc.outstanding || [];
  if (list.length === 0) return [];
  return [
    ['Not yet submitted — not included'],
    ['Week commencing', 'Name', 'Sub-department', 'Status', 'Days entered'],
    ...list.map((o) => [o.week_start, o.name, o.sub_department, weekStatusLabel(o.status), o.days_entered]),
  ];
}

function rateMissingBlock(doc) {
  const list = (doc.person_weeks || []).filter(needsRate);
  if (list.length === 0) return [];
  return [
    ['Needs a rate before payroll'],
    ['Week commencing', 'Name', 'Sub-department', 'Grade'],
    ...list.map((r) => [r.week_start, r.name, r.sub_department, r.grade]),
  ];
}

function unreadableBlock(doc) {
  const list = (doc.person_weeks || []).filter(isUnreadable);
  if (list.length === 0) return [];
  return [
    [SNAPSHOT_UNREADABLE_TEXT],
    ['Week commencing', 'Name', 'Sub-department'],
    ...list.map((r) => [r.week_start, r.name, r.sub_department]),
  ];
}

/**
 * The whole `.csv` text. `sections` = the ticked ids (any order; the output order is ruled).
 * The title is the CSV's first row; then the scope lines; then each block.
 */
export function timesheetExportCsv(doc, sections, title) {
  const want = new Set(sections);
  const blank = [];
  const blocks = [];
  if (want.has('overview')) blocks.push(overviewBlock(doc));
  if (want.has('summary')) blocks.push(summaryBlock(doc));
  if (want.has('detail')) blocks.push(detailBlock(doc));
  const out = outstandingBlock(doc);
  if (out.length) blocks.push(out);
  const rm = rateMissingBlock(doc);
  if (rm.length && (want.has('overview') || want.has('summary'))) blocks.push(rm);
  const un = unreadableBlock(doc);
  if (un.length && (want.has('overview') || want.has('summary'))) blocks.push(un);
  const rows = [
    [departmentLine(doc) || null],
    [exportScopeText(doc)],
    [agreementLabel(doc.agreement_family)],
    [exportedText(doc)],
    blank,
  ];
  for (const b of blocks) rows.push(...b, blank);
  rows.push([NOT_CALCULATED_TEXT]);
  return rowsToCsv([title], rows);
}

// ── PDF ─────────────────────────────────────────────────────────────────────

const mny = (v) => moneyOrAbsent(v);
const num = (v) => (v === null || v === undefined ? EM_DASH : String(v));
const col = (key, label, cell, align = 'left', width = 'auto') => ({ key, label, width, align, cell: (r) => td(cell(r), align) });

function payCols(doc, pick) {
  return payColumns(doc).map((c) => col(c, PAY_COLUMN_LABELS[c] || c, (r) => mny(pick(r).pay[c]), 'right'));
}

function overviewColumns(doc) {
  return [
    col('sub', 'Sub-department', (g) => text(g.sub_department), 'left', '*'),
    col('people', 'People', (g) => num(g.people), 'right'),
    col('days', 'Days', (g) => num(g.days_worked), 'right'),
    col('premium', '6th/7th', (g) => num(g.premium_days), 'right'),
    ...payCols(doc, (g) => g),
    col('total', 'Total', (g) => mny(g.total), 'right'),
    col('missing', 'Rate missing', (g) => (g.rate_missing ? String(g.rate_missing) : EM_DASH), 'right'),
  ];
}

function summaryColumns(doc) {
  return [
    ...(multiWeek(doc) ? [col('week', 'Week', (r) => weekLabel(r.week_start))] : []),
    col('name', 'Name', (r) => text(r.name), 'left', '*'),
    col('grade', 'Grade', (r) => text(r.grade), 'left', '*'),
    col('sub', 'Sub-department', (r) => text(r.sub_department), 'left', '*'),
    col('days', 'Days', (r) => num(r.days_worked), 'right'),
    col('premium', '6th/7th', (r) => num(r.premium_days), 'right'),
    ...payCols(doc, (r) => r),
    col('total', 'Total', (r) => (r.total === null ? noTotalText(r) : mny(r.total)), 'right'),
    col('basis', 'Basis', (r) => basisLabel(r)),
  ];
}

function detailColumns() {
  return [
    col('date', 'Date', (x) => formatDateWeekdayDayMonth(x.day.date)),
    col('name', 'Name', (x) => text(x.person.name), 'left', '*'),
    col('sub', 'Sub-department', (x) => text(x.unit), 'left', '*'),
    col('type', 'Day type', (x) => text(dayTypeText(x))),
    col('callwrap', 'Call–Wrap', (x) => text(timeRange(x.values.unit_call, x.values.unit_wrap))),
    col('inout', 'Pre-call–Final out', (x) => text(timeRange(x.values.in_time, x.values.out_time))),
    col('cam', 'Camera O/T h', (x) => num(x.values.camera_ot_hours), 'right'),
    col('noncam', 'Non-camera O/T h', (x) => num(x.values.non_camera_ot_hours), 'right'),
    col('travel', 'Travel h', (x) => text([x.values.travel_to_hours, x.values.travel_from_hours].filter((h) => h != null).join(' + ') || null), 'right'),
    col('meal', 'Meal pen.', (x) => text(tick(x.values.meal_penalty))),
    col('night', 'Night', (x) => text(tick(x.values.night_penalty))),
    col('job', 'Job', (x) => text(jobText(x.day)), 'left', '*'),
    col('notes', 'Notes', (x) => text(x.day.notes), 'left', '*'),
    col('basis', 'Basis', (x) => basisLabel(x.person)),
  ];
}

const heading = (s) => ({ text: s, fontSize: 12, bold: true, color: '#1e3a5f', margin: [0, 10, 0, 4] });
const para = (s, extra = {}) => ({ text: s, fontSize: 9, margin: [0, 2, 0, 2], ...extra });

function kpiBlock(doc) {
  const t = doc.overview.total;
  const c = doc.counts || {};
  // 🔀 R2 F6 (2026-10-28): the people IN the Total beside it — a priced person-week —
  // never the outstanding names nor the unpriced ones, which the KPIs count separately.
  const people = new Set((doc.person_weeks || []).filter((r) => r.total !== null).map((r) => r.lineage_id)).size;
  const kpi = (value, label, color) => ({ stack: [{ text: value, fontSize: 14, bold: true, ...(color ? { color } : {}) }, { text: label, fontSize: 8 }], width: 'auto', margin: [0, 0, 22, 0] });
  return {
    columns: [
      kpi(mny(t.total), doc.include_projected ? 'Total (incl. projected)' : 'Total to pay'),
      kpi(String(people), 'People paid'),
      kpi(String(c.submitted || 0), 'Submitted'),
      ...(doc.include_projected ? [kpi(String(c.projected || 0), 'Projected')] : [kpi(String(c.outstanding || 0), 'Not yet submitted')]),
      kpi(String(c.rate_missing || 0), 'Rate missing', c.rate_missing ? '#b45309' : null),
      ...(c.snapshot_unreadable ? [kpi(String(c.snapshot_unreadable), 'Snapshot unreadable', '#b45309')] : []),
    ],
    margin: [0, 0, 0, 8],
  };
}

const NOUNS = {
  overview: { one: 'sub-department', many: 'sub-departments' },
  summary: { one: 'person-week', many: 'person-weeks' },
  detail: { one: 'day', many: 'days' },
};

/** The pdfmake document for the ticked sections. */
export function timesheetExportPdfDoc(doc, sections, title) {
  const want = EXPORT_SECTIONS.map((s) => s.id).filter((id) => sections.includes(id));
  const spec = {
    overview: () => ({ rows: (doc.overview && doc.overview.groups) || [], columns: overviewColumns(doc) }),
    summary: () => ({ rows: doc.person_weeks || [], columns: summaryColumns(doc) }),
    detail: () => ({ rows: detailRows(doc), columns: detailColumns() }),
  };
  const first = want[0] || 'summary';
  const firstSpec = spec[first]();
  const pdf = buildListDoc({
    ...firstSpec,
    noun: NOUNS[first],
    headerTitle: title,
    departmentName: departmentLine(doc),
    filterSummary: exportScopeText(doc),
    subject: 'Timesheet export',
    generatedAt: doc.exported_at || null,
    showScopeLine: false,
  });
  // The header block, then the KPIs, then the first section (its table is the last item).
  const table = pdf.content.pop();
  const meta = [agreementLabel(doc.agreement_family), exportedText(doc)].filter(Boolean).join(' · ');
  pdf.content.push(para(meta, { color: '#666666' }), kpiBlock(doc), heading(EXPORT_SECTIONS.find((s) => s.id === first).label), table);
  const totalLine = () => para(`${doc.department_name || 'Department'} total: ${mny(doc.overview.total.total)} · ${doc.overview.total.days_worked} ${doc.overview.total.days_worked === 1 ? 'day' : 'days'}`, { bold: true, fontSize: 10 });
  if (first === 'overview') pdf.content.push(totalLine());
  for (const id of want.slice(1)) {
    const s = spec[id]();
    const content = buildListContent({ ...s, noun: NOUNS[id], headerTitle: title });
    pdf.content.push(heading(EXPORT_SECTIONS.find((x) => x.id === id).label), content[content.length - 1]);
    if (id === 'overview') pdf.content.push(totalLine());
  }
  if ((doc.outstanding || []).length) {
    pdf.content.push(heading('Not yet submitted — not included'));
    pdf.content.push(para(doc.outstanding.map((o) => `${o.name} (${o.sub_department}${multiWeek(doc) ? `, ${weekLabel(o.week_start)}` : ''}) · ${weekStatusLabel(o.status)}, ${o.days_entered} ${o.days_entered === 1 ? 'day' : 'days'} entered`).join('   ·   ')));
  }
  const missing = (doc.person_weeks || []).filter(needsRate);
  if (missing.length) {
    pdf.content.push({ ...heading('Needs a rate before payroll'), color: '#b45309' });
    pdf.content.push(para(`${missing.map((r) => `${r.name} (${r.sub_department}${r.grade ? `, ${r.grade}` : ''})`).join('   ·   ')} — set the rate in Budget → Crew Grades.`));
  }
  const unreadable = (doc.person_weeks || []).filter(isUnreadable);
  if (unreadable.length) {
    pdf.content.push({ ...heading(SNAPSHOT_UNREADABLE_TEXT), color: '#b45309' });
    pdf.content.push(para(unreadable.map((r) => `${r.name} (${r.sub_department}${multiWeek(doc) ? `, ${weekLabel(r.week_start)}` : ''})`).join('   ·   ')));
  }
  pdf.content.push(para(NOT_CALCULATED_TEXT, { fontSize: 8, color: '#666666', margin: [0, 12, 0, 0] }));
  return pdf;
}

// ── names ───────────────────────────────────────────────────────────────────

/** The title on the sheet / PDF. */
export function exportTitle(doc, { payrollRun = false } = {}) {
  return payrollRun ? `Payroll run — ${weekLabel(doc.from)}` : `Timesheets — ${rangeLabel(doc)}`;
}

/**
 * `timesheets-<from>[_to_<to>]-<today>.csv|.pdf` / `payroll-run-<week>-<today>…`
 * `format` is `'pdf'` or the CSV: `'csv'` is canonical, the old `'excel'` token is
 * an ALIAS (anything that is not `'pdf'` is the CSV).
 */
export function exportFilename(doc, format, { payrollRun = false, now = new Date() } = {}) {
  const span = doc.from === doc.to ? doc.from : `${doc.from}_to_${doc.to}`;
  const stem = payrollRun ? `payroll-run-${doc.from}` : `timesheets-${span}`;
  return `${stem}-${toLocalISO(now)}${format === 'pdf' ? '.pdf' : CSV_EXT}`;
}

/** Save the CSV (the BOM'd house shape). */
export function downloadTimesheetCsv(doc, sections, opts = {}) {
  downloadTextFile(timesheetExportCsv(doc, sections, exportTitle(doc, opts)), exportFilename(doc, 'csv', opts), CSV_MIME);
}

// ── THE CREW'S OWN EXPORT (owner 2026-11-01: "no money yet" — HOURS ONLY) ─────────────────
// The document is `GET /api/timesheets/my-export` (`server/helpers/timesheetCrewExport.js`):
// `{ department_name, production_name, from, to, exported_at, weeks: [{ week_start, people }] }`,
// each person the `/my-week` crew allowlist. One block per person-week, labelled Draft / Sent to
// buyer / Taken over by buyer / Approved (the screen's words); a week total of HOURS. 💷 No £, no
// rate, no pay line: the response carries none and nothing here invents one.

export const CREW_HOURS_ONLY_TEXT = 'Your own hours, day by day. No pay figures.';

/** The week's label, in My timesheet's words (`CrewTimesheet.js`'s status pill). */
export function crewWeekLabel(person) {
  if (person.status === 'submitted') return 'Approved';
  return CREW_STATE_LABELS[person.crew_state] || CREW_STATE_LABELS.draft;
}

const hoursText = (n) => (n === null ? null : String(n));

/** One printable row per day of one crew person-week — every value as My timesheet shows it. */
export function crewDayRow(day) {
  const f = (k) => (day.fields && day.fields[k] ? day.fields[k] : {});
  const v = (k) => (f(k).value === undefined ? null : f(k).value);
  const wrap = v('unit_wrap');
  const lunch = v('meal_start') || v('meal_end') ? `${v('meal_start') || '?'}–${v('meal_end') || '?'}` : null;
  const dayType = day.away
    ? `Away · ${awayReasonLabel(day.away.reason)}`
    : formatFieldValue('day_type', v('day_type')) || (day.default_rest ? 'Rest' : null);
  const conflicts = (day.conflicts || []).map((k) => fieldLabel(k)).filter(Boolean);
  const notes = [day.notes || null, conflicts.length ? `▲ differs from the unit timecard: ${conflicts.join(', ')}` : null]
    .filter(Boolean).join(' · ') || null;
  return {
    date: day.date,
    day: formatDateWeekdayDayMonth(day.date),
    department: day.unit ? day.unit.name : null,
    day_type: dayType,
    pre_call: v('in_time'),
    call: v('unit_call'),
    wrap: wrap ? `${wrap}${f('unit_wrap').auto ? ' auto' : ''}` : null,
    final_out: v('out_time'),
    lunch,
    ot_hours: sumHours(v('camera_ot_hours'), v('non_camera_ot_hours')),
    travel_hours: sumHours(v('travel_to_hours'), v('travel_from_hours')),
    job: (day.gag && day.gag.title) || day.job_text || null,
    notes,
  };
}

/** Every person-week in the document, in order: `{ week_start, person, rows, totals }`. */
export function crewExportBlocks(doc) {
  const out = [];
  for (const w of doc.weeks || []) {
    for (const person of w.people || []) {
      const rows = (person.days || []).map(crewDayRow);
      out.push({
        week_start: w.week_start,
        person,
        rows,
        totals: { ot_hours: sumHours(...rows.map((r) => r.ot_hours)), travel_hours: sumHours(...rows.map((r) => r.travel_hours)) },
      });
    }
  }
  return out;
}

const crewRangeLabel = (doc) => {
  const n = (doc.weeks || []).length;
  return n <= 1 ? weekLabel(doc.from) : `${weekLabel(doc.from)} – ${weekLabel(doc.to)} (${n} weeks)`;
};
const crewBlockHeading = (b) => `${b.person.name} · ${weekLabel(b.week_start)} · ${crewWeekLabel(b.person)}`;
export const CREW_NO_WEEKS_TEXT = 'No timesheet for you in these weeks.';
const CREW_HEADERS = ['Day', 'Department', 'Day type', 'Pre-call', 'Call', 'Wrap', 'Final out', 'Lunch', 'O/T hrs', 'Travel hrs', 'Main job / task', 'Notes'];
const crewCells = (r) => [r.day, r.department, r.day_type, r.pre_call, r.call, r.wrap, r.final_out, r.lunch, hoursText(r.ot_hours), hoursText(r.travel_hours), r.job, r.notes];

/** "My timesheets — w/c 2 Nov 2026" (the CSV's first row / the PDF's header). */
export function crewExportTitle(doc) {
  return `My timesheets — ${crewRangeLabel(doc)}`;
}

/** The whole crew `.csv` text: scope lines, then one titled block per person-week. */
export function crewExportCsv(doc, title = crewExportTitle(doc)) {
  const blocks = crewExportBlocks(doc);
  const rows = [[departmentLine(doc) || null], [CREW_HOURS_ONLY_TEXT], [exportedText(doc)], []];
  if (blocks.length === 0) rows.push([CREW_NO_WEEKS_TEXT]);
  for (const b of blocks) {
    rows.push([crewBlockHeading(b)], CREW_HEADERS, ...b.rows.map(crewCells));
    rows.push(['Week total', null, null, null, null, null, null, null, hoursText(b.totals.ot_hours), hoursText(b.totals.travel_hours), null, null], []);
  }
  return rowsToCsv([title], rows);
}

function crewColumns() {
  return [
    col('day', 'Day', (r) => text(r.day)),
    col('dept', 'Department', (r) => text(r.department), 'left', '*'),
    col('type', 'Day type', (r) => text(r.day_type)),
    col('pre', 'Pre-call', (r) => text(r.pre_call)),
    col('call', 'Call', (r) => text(r.call)),
    col('wrap', 'Wrap', (r) => text(r.wrap)),
    col('out', 'Final out', (r) => text(r.final_out)),
    col('lunch', 'Lunch', (r) => text(r.lunch)),
    col('ot', 'O/T hrs', (r) => num(r.ot_hours), 'right'),
    col('travel', 'Travel hrs', (r) => num(r.travel_hours), 'right'),
    col('job', 'Main job / task', (r) => text(r.job), 'left', '*'),
    col('notes', 'Notes', (r) => text(r.notes), 'left', '*'),
  ];
}
const CREW_NOUN = { one: 'day', many: 'days' };

/** The crew pdfmake document: A4 landscape, one page per person-week, each with its week total. */
export function crewExportPdfDoc(doc, title = crewExportTitle(doc)) {
  const blocks = crewExportBlocks(doc);
  const columns = crewColumns();
  const pdf = buildListDoc({
    rows: blocks.length ? blocks[0].rows : [],
    columns,
    noun: CREW_NOUN,
    headerTitle: title,
    departmentName: departmentLine(doc),
    filterSummary: `${crewRangeLabel(doc)} · ${CREW_HOURS_ONLY_TEXT}`,
    subject: 'My timesheets',
    generatedAt: doc.exported_at || null,
    showScopeLine: false,
  });
  const table = pdf.content.pop();
  const total = (b) => para(`Week total: O/T ${num(b.totals.ot_hours)} hrs · Travel ${num(b.totals.travel_hours)} hrs · ${crewWeekLabel(b.person)}`, { bold: true, fontSize: 10 });
  pdf.content.push(para(exportedText(doc), { color: '#666666' }));
  if (blocks.length === 0) {
    pdf.content.push(para(CREW_NO_WEEKS_TEXT));
    return pdf;
  }
  blocks.forEach((b, i) => {
    const h = heading(crewBlockHeading(b));
    if (i === 0) {
      pdf.content.push(h, table);
    } else {
      const content = buildListContent({ rows: b.rows, columns, noun: CREW_NOUN, headerTitle: title });
      pdf.content.push({ ...h, pageBreak: 'before' }, content[content.length - 1]);
    }
    pdf.content.push(total(b));
  });
  return pdf;
}

/** `my-timesheets-<from>[_to_<to>]-<today>.csv|.pdf` */
export function crewExportFilename(doc, format, { now = new Date() } = {}) {
  const span = doc.from === doc.to ? doc.from : `${doc.from}_to_${doc.to}`;
  return `my-timesheets-${span}-${toLocalISO(now)}${format === 'pdf' ? '.pdf' : CSV_EXT}`;
}

/** Save the crew CSV (the BOM'd house shape). */
export function downloadCrewExportCsv(doc, opts = {}) {
  downloadTextFile(crewExportCsv(doc), crewExportFilename(doc, 'csv', opts), CSV_MIME);
}
